import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Shirt,
  Store,
  ShoppingBag,
  Gem,
  Car,
  type LucideIcon,
} from "lucide-react";
import LazyVideo from "@/components/LazyVideo";

type Item = {
  label: string;
  icon: LucideIcon;
  href: string;
  /** A video already used on that solution page's hero. */
  video: string;
};

const solutions: Item[] = [
  { label: "Fashion", icon: Shirt, href: "/solutions/fashion", video: "https://cdn.pixro.ai/ca111b16-f24d-4237-bc90-1f33289b7650.mp4" },
  { label: "Ecommerce", icon: Store, href: "/solutions/ecommerce", video: "https://cdn.pixro.ai/6cec104d-7ba5-4796-9c12-5998711e78fd.mp4" },
  { label: "Accessories", icon: ShoppingBag, href: "/solutions/accessories", video: "https://cdn.pixro.ai/2596fc7d-07dd-4030-a3e7-47cb296074f1.mp4" },
  { label: "Jewellery", icon: Gem, href: "/solutions/jewellery", video: "https://cdn.pixro.ai/60ec21c8-842b-4a56-a536-d26f4b40a981.mp4" },
  { label: "Automobile", icon: Car, href: "/solutions/automobile", video: "https://cdn.pixro.ai/4193fa3e-2fa9-4fe0-9fc3-233b451e44c7.mp4" },
];


interface SolutionsMenuProps {
  open: boolean;
  onClose: () => void;
}

const SolutionsMenu = ({ open, onClose }: SolutionsMenuProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [hoveredItem, setHoveredItem] = useState<Item | null>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const activeItem = hoveredItem ?? solutions[0];

  return (
    <div
      ref={ref}
      className="fixed left-1/2 top-10 z-50 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-2xl border border-white/10 bg-card/40 backdrop-blur-2xl shadow-2xl pb-[24px] pt-[24px] pl-[24px] pr-[24px] w-auto"
    >
      <div className="flex gap-8 items-stretch">
        <div className="flex-shrink-0">
          <h4 className="text-xs font-semibold tracking-widest text-muted-foreground mb-[4px] mx-[8px]">
            SOLUTIONS
          </h4>
          <ul className="flex flex-col gap-1">
            {solutions.map((tool) => {
              const Icon = tool.icon;
              return (
                <li key={tool.label}>
                  <Link
                    to={tool.href}
                    onClick={onClose}
                    onMouseEnter={() => setHoveredItem(tool)}
                    className={`flex items-center gap-3 whitespace-nowrap rounded-lg px-2 py-2 text-sm transition-colors hover:bg-muted/40 ${pathname === tool.href ? "text-primary hover:text-primary" : "text-foreground/90 hover:text-foreground"}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tool.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="flex-shrink-0 flex items-center justify-start">
          <div className="aspect-square w-[220px] overflow-hidden rounded-2xl">
            <LazyVideo
              key={activeItem.video}
              src={activeItem.video}
              aria-label={activeItem.label}
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SolutionsMenu;
